import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtGBP, fmtMoney, fmtDate, statusTone } from "@/lib/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { X, ExternalLink } from "lucide-react";

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-slate-800/60">
      <span className="text-[11px] font-mono uppercase tracking-widest text-slate-500 shrink-0">{label}</span>
      <span className={`text-sm text-right text-slate-200 ${mono ? "mono-num" : ""}`}>{value ?? "—"}</span>
    </div>
  );
}

export default function LotDrawer({ lotId, open, onClose }) {
  const { data: lot } = useQuery({
    queryKey: ["lot", lotId],
    queryFn: async () => (await api.get(`/lots/${encodeURIComponent(lotId)}`)).data,
    enabled: !!lotId && open,
  });

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent data-testid="lot-drawer" className="bg-[#121824] border-l border-slate-800 text-slate-200 w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-serif text-2xl text-slate-100 pr-8">{lot?.title || "Lot detail"}</SheetTitle>
          <SheetDescription className="text-xs font-mono text-slate-500">{lot?.lot_id || "Canonical lot record"}</SheetDescription>
        </SheetHeader>
        {lot && (
          <div className="mt-4 space-y-5">
            <div className="flex items-center gap-2">
              <span className={`al-chip ${statusTone[lot.status] || ""}`}>{lot.status}</span>
              {lot.review_required && <span className="al-chip bg-violet-500/15 text-violet-300 border border-violet-500/30">Review</span>}
              <span className="al-chip border border-slate-700 text-slate-400">{lot.auction_house}</span>
            </div>

            <div>
              <p className="font-serif text-lg text-amber-400">{lot.artist}</p>
              <p className="text-sm text-slate-400">{lot.work_year_or_date} · {lot.medium}</p>
            </div>

            <div className="al-card p-4">
              <p className="al-label">Pricing</p>
              <Row label="GBP (normalized)" value={fmtGBP(lot.result_price_gbp)} mono />
              <Row label="Result (native)" value={fmtMoney(lot.result_price, lot.currency)} mono />
              <Row label="Price basis" value={lot.result_price_basis || "—"} />
              <Row label="Hammer" value={fmtMoney(lot.hammer_price, lot.currency)} mono />
              <Row label="Incl. premium" value={fmtMoney(lot.buyers_premium_inclusive_price, lot.currency)} mono />
              <Row label="Estimate" value={`${fmtMoney(lot.estimate_low, lot.currency)} – ${fmtMoney(lot.estimate_high, lot.currency)}`} mono />
              {lot.status === "Unsold" && <Row label="Highest bid" value={fmtMoney(lot.highest_bid_if_unsold, lot.currency)} mono />}
              <Row label="FX rate" value={lot.exchange_rate_to_gbp ? `${lot.exchange_rate_to_gbp} (${lot.exchange_rate_date})` : "—"} mono />
            </div>

            <div className="al-card p-4">
              <p className="al-label">Sale</p>
              <Row label="Sale" value={lot.sale_name} />
              <Row label="Sale ID" value={lot.sale_number_or_id} mono />
              <Row label="Location" value={lot.sale_location} />
              <Row label="Date" value={fmtDate(lot.sale_date)} />
              <Row label="Lot number" value={lot.lot_number} mono />
              <Row label="Venue" value={lot.source_venue_name} />
            </div>

            <div className="al-card p-4">
              <p className="al-label">Provenance & identifiers</p>
              <Row label="Lot ID" value={lot.lot_id} mono />
              <Row label="Dimensions" value={lot.dimensions} />
              <Row label="Category" value={lot.category} />
              <Row label="Original artist" value={lot.original_artist_name} />
              <Row label="Data source" value={lot.data_source} />
              <Row label="Quality" value={lot.record_quality_status} />
              {lot.review_reason && <Row label="Review reason" value={lot.review_reason} />}
            </div>

            {lot.lot_url && (
              <a data-testid="lot-source-link" href={lot.lot_url} target="_blank" rel="noreferrer" className="al-btn-ghost w-full">
                <ExternalLink className="w-4 h-4" /> View source lot
              </a>
            )}
            {lot.audit_history?.length > 0 && (
              <div className="al-card p-4">
                <p className="al-label">Audit history</p>
                {lot.audit_history.map((a, i) => (
                  <Row key={i} label={a.field} value={`${a.old_value} → ${a.new_value}`} mono />
                ))}
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
